import type { Listing } from '@smg-real-estate/hg-rets-schema';

/**
 * Types for the Intelligence Hub similar-listings API response.
 * These use partial/optional shapes (e.g. {@link ListingUrls}) and are distinct from
 * insertion types in `src/types.ts`, where the same names require all locales.
 */
export const SIMILAR_LISTINGS_SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'] as const;

export type SupportedLanguage = (typeof SIMILAR_LISTINGS_SUPPORTED_LANGUAGES)[number];

/** Platform keys returned in similar-listing `listingLinks`. */
export enum SupportedPlatform {
  HOMEGATE = 'homegate',
  IMMOSCOUT24 = 'immoscout24',
  FLATFOX = 'flatfox',
}

/** Per-locale listing URLs; any locale may be absent in the API payload. */
export type ListingUrls = Partial<Record<SupportedLanguage, string>>;

/** Per-platform listing URLs; any platform may be absent in the API payload. */
export type ListingLinks = Partial<Record<SupportedPlatform, ListingUrls>>;

export interface SimilarListingResponseItem {
  listingId: string;
  address: Listing['address'];
  characteristics: Listing['characteristics'];
  prices: Listing['prices'];
  listingLinks: ListingLinks;
  imageUrl?: string;
  numberOfImages: number;
  /** Last-7-day form+call leads. IH emits 0 for null, missing, negative, or non-integer values. */
  leads: number;
  /** Last-7-day impressions. IH emits 0 for null, missing, negative, or non-integer values. */
  impressions: number;
}

export interface SimilarListingsResponse {
  listingId: string;
  propertyReferenceId: string;
  similarListingCustomerIsIdentifiable: boolean;
  similarListingsTotal: number;
  similarListings: SimilarListingResponseItem[];
  /**
   * Max 7-day form+call leads across the full Analytics similar-listings array
   * (before the max-10 cap and the NBA online filter). Own listing is excluded.
   */
  leadsMax: number;
  /**
   * Max 7-day impressions across the full Analytics similar-listings array
   * (before the max-10 cap and the NBA online filter). Own listing is excluded.
   */
  impressionsMax: number;
}
